import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import { Sparkles, Eye, EyeOff, Mail, Lock, LogIn, X, AlertTriangle, RefreshCw } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';

const SSO_ERROR_MESSAGES = {
    invalid_state: 'SSO session expired. Please try again.',
    missing_code: 'SSO authorization was cancelled.',
    token_exchange_failed: 'Could not complete SSO sign-in. Please try again.',
    no_access_token: 'SSO provider did not return a valid token.',
    user_fetch_failed: 'Could not retrieve your profile from the SSO provider.',
    no_email: 'No email address was returned from SSO.',
    no_account: 'No account found for your email address. Please sign up first or contact your admin.',
    no_matching_org: 'No account found for your email address. Please sign up first or contact your admin.',
    network_error: 'Network error during SSO. Please try again.',
    internal_error: 'An unexpected error occurred during SSO. Please try again.',
};

export default function Login() {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [showPw, setShowPw] = useState(false);
    const [loading, setLoading] = useState(false);
    const [ssoAlert, setSsoAlert] = useState(null);
    const [emailNotVerified, setEmailNotVerified] = useState(false);
    const [resending, setResending] = useState(false);
    const { login, loginWithGoogle } = useAuth();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();

    useEffect(() => {
        const ssoError = searchParams.get('sso_error');
        if (ssoError) {
            setSsoAlert(SSO_ERROR_MESSAGES[ssoError] || 'SSO sign-in failed. Please try again.');
            searchParams.delete('sso_error');
            setSearchParams(searchParams, { replace: true });
        }
    }, [searchParams, setSearchParams]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!email || !password) return toast.error('Please fill in all fields');
        setLoading(true);
        setEmailNotVerified(false);
        try {
            await login(email, password);
            navigate('/dashboard');
        } catch (err) {
            const detail = err.response?.data?.detail || '';
            if (err.response?.status === 403 && detail.toLowerCase().includes('verify your email')) {
                setEmailNotVerified(true);
            } else {
                toast.error(detail || 'Login failed');
            }
        } finally {
            setLoading(false);
        }
    };

    const handleResendVerification = async () => {
        setResending(true);
        try {
            await axios.post(`${API}/api/auth/resend-verification`, { email });
            toast.success('Verification email sent! Check your inbox.');
        } catch {
            toast.error('Could not resend verification email. Please try again later.');
        } finally {
            setResending(false);
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center p-4" style={{ backgroundColor: '#FAFAF5' }}>
            <div className="w-full max-w-md">
                <div className="text-center mb-8 animate-fade-in">
                    <div className="inline-flex items-center justify-center w-14 h-14 bg-stone-900 rounded-2xl shadow-lg mb-4">
                        <Sparkles className="w-7 h-7 text-white" />
                    </div>
                    <h1 className="text-3xl font-bold text-stone-900" style={{ fontFamily: 'Manrope, system-ui, sans-serif' }}>
                        Nurture <span className="text-stone-500">CRM</span>
                    </h1>
                    <p className="text-stone-500 mt-2 text-sm">AI-powered relationship intelligence</p>
                </div>

                {ssoAlert && (
                    <div className="mb-4 bg-red-50 border border-red-200 rounded-xl p-4 flex items-start gap-3 animate-fade-in">
                        <AlertTriangle className="w-5 h-5 text-red-500 shrink-0 mt-0.5" />
                        <div className="flex-1">
                            <p className="text-sm font-medium text-red-800">Sign-in failed</p>
                            <p className="text-sm text-red-600 mt-0.5">{ssoAlert}</p>
                        </div>
                        <button onClick={() => setSsoAlert(null)} className="text-red-400 hover:text-red-600 transition-colors shrink-0">
                            <X className="w-4 h-4" />
                        </button>
                    </div>
                )}

                {emailNotVerified && (
                    <div className="mb-4 bg-amber-50 border border-amber-200 rounded-xl p-4 animate-fade-in">
                        <div className="flex items-start gap-3">
                            <Mail className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
                            <div className="flex-1">
                                <p className="text-sm font-medium text-amber-800">Email not verified</p>
                                <p className="text-sm text-amber-700 mt-0.5">Please check your inbox for the verification link before signing in.</p>
                                <button
                                    onClick={handleResendVerification}
                                    disabled={resending}
                                    className="mt-2 inline-flex items-center gap-1.5 text-sm font-medium text-amber-800 hover:text-amber-900 underline underline-offset-2"
                                >
                                    <RefreshCw className={`w-3.5 h-3.5 ${resending ? 'animate-spin' : ''}`} />
                                    {resending ? 'Sending...' : 'Resend verification email'}
                                </button>
                            </div>
                            <button onClick={() => setEmailNotVerified(false)} className="text-amber-400 hover:text-amber-600 transition-colors shrink-0">
                                <X className="w-4 h-4" />
                            </button>
                        </div>
                    </div>
                )}

                <div className="bg-white rounded-2xl border border-stone-200 shadow-sm p-8 animate-fade-in stagger-1">
                    <h2 className="text-xl font-semibold text-stone-900 mb-6" style={{ fontFamily: 'Manrope, system-ui, sans-serif' }}>
                        Sign in to your account
                    </h2>

                    <button
                        onClick={loginWithGoogle}
                        data-testid="google-login-btn"
                        className="w-full flex items-center justify-center gap-3 px-4 py-2.5 border border-stone-200 rounded-lg text-stone-700 font-medium hover:bg-stone-50 transition-colors mb-3"
                    >
                        <svg className="w-5 h-5" viewBox="0 0 24 24">
                            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                        </svg>
                        Continue with Google
                    </button>

                    <button
                        onClick={() => { window.location.href = `${process.env.REACT_APP_BACKEND_URL}/api/auth/sso/authorize`; }}
                        className="w-full flex items-center justify-center gap-3 px-4 py-2.5 border border-stone-200 rounded-lg text-stone-700 font-medium hover:bg-stone-50 transition-colors mb-6"
                    >
                        <LogIn className="w-5 h-5 text-stone-500" />
                        Sign in with Multiplai
                    </button>

                    <div className="relative mb-6">
                        <div className="absolute inset-0 flex items-center">
                            <div className="w-full border-t border-stone-200" />
                        </div>
                        <div className="relative flex justify-center text-xs">
                            <span className="bg-white px-3 text-stone-400">or sign in with email</span>
                        </div>
                    </div>

                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div>
                            <label className="block text-xs font-semibold text-stone-500 uppercase tracking-wider mb-1.5">Email address</label>
                            <div className="relative">
                                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
                                <input
                                    type="email"
                                    value={email}
                                    onChange={e => setEmail(e.target.value)}
                                    placeholder="you@company.com"
                                    data-testid="login-email-input"
                                    className="w-full pl-10 pr-4 py-2.5 text-sm border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-stone-900/10 focus:border-stone-400 transition-all"
                                />
                            </div>
                        </div>
                        <div>
                            <label className="block text-xs font-semibold text-stone-500 uppercase tracking-wider mb-1.5">Password</label>
                            <div className="relative">
                                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
                                <input
                                    type={showPw ? 'text' : 'password'}
                                    value={password}
                                    onChange={e => setPassword(e.target.value)}
                                    placeholder="Enter your password"
                                    data-testid="login-password-input"
                                    className="w-full pl-10 pr-10 py-2.5 text-sm border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-stone-900/10 focus:border-stone-400 transition-all"
                                />
                                <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600">
                                    {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                </button>
                            </div>
                        </div>
                        <button
                            type="submit"
                            disabled={loading}
                            data-testid="login-submit-btn"
                            className="w-full py-2.5 bg-stone-900 text-white font-medium rounded-lg hover:bg-stone-800 transition-colors shadow-sm disabled:opacity-50 disabled:cursor-not-allowed mt-2"
                        >
                            {loading ? 'Signing in...' : 'Sign in'}
                        </button>
                    </form>

                    <p className="text-center text-sm text-stone-500 mt-6">
                        Don't have an account?{' '}
                        <Link to="/register" className="text-stone-900 hover:text-stone-700 font-medium">Sign up</Link>
                    </p>
                </div>
            </div>
        </div>
    );
}
